/**
 * SignInGuestForm.jsx
 * Created by michaelbray on 2/14/18.
 * Updated by Destin Frasier on 1/16/19.
 */

import React from 'react';
import PropTypes from 'prop-types';
import * as Icons from '../../../sharedComponents/icons/Icons';
import { FlexCol, FlexRow } from '../../../sharedComponents/FlexGrid';
import Button, { ButtonGroup } from '../../../sharedComponents/Button';
import Loading from '../../../sharedComponents/Loading';
import Alert from '../../../../components/sharedComponents/Alert';
import Heading from '../../../sharedComponents/Heading';

const propTypes = {
    disableAccountFeatures: PropTypes.bool,
    errorText: PropTypes.string,
    create: PropTypes.func,
    reset: PropTypes.func,
    confirmSignIn: PropTypes.func,
    mfaRequired: PropTypes.bool,
    mfaValidation: PropTypes.bool
};

export default class SignInGuestForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            email: '',
            password: '',
            processing: false
        };

        this.emailTextChange = this.emailTextChange.bind(this);
        this.passwordTextChange = this.passwordTextChange.bind(this);
        this.submitForm = this.submitForm.bind(this);
    }

    componentDidMount() {
        this.email.focus();
        this.showLoginModal = true;
    }

    componentDidUpdate(){
        if (!!this.state.processing && (this.props.errorText || this.props.mfaRequired)){
            this.setState({
                processing: false
            });
        }
    }

    emailTextChange(e) {
        this.setState({
            email: e.target.value
        });
    }

    passwordTextChange(e) {
        this.setState({
            password: e.target.value
        });
    }

    submitForm(e) {
        e.preventDefault();
        this.setState({
            processing: true
        });

        this.props.confirmSignIn({
            email: this.state.email,
            password: this.state.password
        });
    }

    render() {
        let error = null;
        let loading = null;
        if (this.state.processing) {
            loading = (
                <div className="rec-sign-loading">
                    <Loading />
                </div>
            );
        }

        if (this.props.errorText) {
            error = (
                <Alert
                    type="error"
                    className="mb-2">
                    {this.props.errorText}
                </Alert>
            );
        }

        let mfa = null;
        if (this.props.mfaRequired) {
            mfa = (
                <Alert
                    type="warning"
                    className="mb-2">
                    We don&apos;t recognize the device you&apos;re trying to login from.
                    Please check your email to verify your identity and authorize this device.
                </Alert>
            );
        }

        let mfaValidationText = null;
        let reset = null;
        let create = null;
        if (this.props.mfaValidation) {
            mfaValidationText = (
                <Alert
                    type="success"
                    className="mb-2">
                        Your device has been successfully validated, please log in to complete this process.
                </Alert>
            );
        }
        else {
            reset = (
                <ButtonGroup align="right">
                    <Button
                        appearance="link"
                        onClick={this.props.reset}
                        disabled={this.props.disableAccountFeatures}
                        className="rec-acct-modal-forgot-password"
                        title="Forgot Password"
                        aria-label="Forgot Password">
                        Forgot Password?
                    </Button>
                </ButtonGroup>
            );
            create = (
                <div className="rec-acct-modal-existing-account">
                    <p>Don&apos;t have an account?</p>
                    <Button
                        appearance="link"
                        onClick={this.props.create}
                        disabled={this.props.disableAccountFeatures}
                        title="Sign Up"
                        aria-label="Sign Up">
                        Sign Up
                    </Button>
                </div>
            );
        }

        let loginDisabled = null;
        if (this.props.disableAccountFeatures) {
            loginDisabled = (
                <Alert
                    type="error"
                    className="mb-2">
                    Please be aware that you cannot make or change any reservations nor can you log in to your account
                    or create a new one. Account access and reservations are expected to be available starting on
                    October 5 at 10 a.m. (Eastern Time).
                </Alert>
            );
        }

        // Change the layout of the page if the notification is active
        let noAlert = '';

        // add "!" to this to hide the notification
        if (this.props.disableAccountFeatures) {
            noAlert = 'no-alert';
        }

        return (
            <FlexRow justifyContent="center">
                <FlexCol sm={8} className="rec-acct-modal-log-in-guest-body">
                    <Heading
                        headingLevel={1}
                        appearance="display"
                        className="rec-acct-modal-title"
                        id="signInGuestModalHeading">
                        Log In
                    </Heading>
                    {create}
                    {loginDisabled}
                    {loading}
                    {error}
                    {mfa}
                    {mfaValidationText}
                    <FlexRow>
                        <FlexCol md={6} className="rec-acct-modal-left-rail">
                        <div className="rec-acct-modal-subhead">
                            Returning Visitors
                        </div>
                        <p>Sign in to your Recreation.gov account to access your information, see all your reservations, and check out faster.</p>
                            <form onSubmit={this.submitForm}>
                                <div className="form-item-wrap">
                                    <input
                                        placeholder="Email Address"
                                        id="rec-acct-sign-in-email-address"
                                        disabled={this.props.disableAccountFeatures}
                                        ref={(email) => this.email = email}
                                        onChange={(event) => this.emailTextChange(event)}
                                        className="rec-acct-email" />
                                </div>
                                <div className="form-item-wrap">
                                    <input
                                        placeholder="Password"
                                        id="rec-acct-sign-in-password"
                                        disabled={this.props.disableAccountFeatures}
                                        type="password"
                                        ref={(password) => this.password = password}
                                        onChange={(event) => this.passwordTextChange(event)}
                                        className="rec-acct-password"
                                        autoComplete="off" />
                                </div>
                                <FlexRow>
                                    <FlexCol md={6}>
                                        <Button
                                            size="lg"
                                            shouldFitContainer
                                            className="rec-acct-log-in-btn"
                                            type="submit"
                                            disabled={this.state.processing || this.props.disableAccountFeatures}
                                            title="Log In"
                                            aria-label="Log In">
                                            Log In
                                        </Button>
                                    </FlexCol>
                                    <FlexCol md={6}>
                                        {reset}
                                    </FlexCol>
                                </FlexRow>
                            </form>
                        </FlexCol>

                        <FlexCol md={1} className={`rec-acct-modal-split-line-wrap ${noAlert}`}>
                            <div className="rec-acct-modal-split-line" />
                        </FlexCol>

                        <FlexCol md={5} className={`rec-acct-modal-right-rail ${noAlert}`}>
                            <div className="rec-acct-modal-subhead">
                                Guests
                            </div>
                            <p>
                                No account? No problem! You can still check out as a guest, and sign up for an account later if you&#39;re interested.
                            </p>
                            <Button
                                appearance="secondary"
                                size="lg"
                                iconAfterElement={<Icons.IconArrowForward />}
                                className="rec-acct-modal-guest-btn"
                                onClick={this.props.continueAsGuest}
                                title="Continue as Guest"
                                aria-label="Continue as Guest">
                                Continue as Guest
                            </Button>
                        </FlexCol>
                    </FlexRow>
                </FlexCol>
            </FlexRow>
        );
    }
}

SignInGuestForm.propTypes = propTypes;
